import { Container, Box } from '@mui/material';
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import Header from './Header';
import Home from './LandingPage/Home';
import { useDispatch, useSelector } from 'react-redux';
import { UnsupportedBrowser } from './UnsupportedBrowser';
import CommonDialog from './CommonDialog';
import LoadingScreen from './LoadingScreen';
import { Master } from '../data/Constants';
import { setActiveWorkItem } from '../store/states/procos';
import { getLoggedInUserDetails } from '../store/states/UserSlice';
import { AppDispatch, AppState } from 'store/AppStore';
import { useAuth } from 'oidc-react';
import { Unauthenticated } from './Unauthenticated';
import { Unauthorized } from './Unauthorized';
import Procos from './Procos/Procos';
import Pdm from './Pdm/Pdm';
import { FeaturesMarketAssociation } from './Procos/FeaturesMarketAssociation';
import AppTabs from './AppTabs';
import { MultiNumericFeatureManagement } from './LandingPage/MultinumericFeatureManagement';
import { ToastContainer } from 'react-toastify';
import { Logout } from './Logout';
import Category from './category/Category';
import type { IUserDetails } from '../../types';

//Renders the Middle Content of Application based on Authorized User Details
const getMiddleContent = (
  token: string | undefined,
  userDetails: IUserDetails | null
): React.ReactNode => {
  if (!token) {
    // Unauthenticated
    return (
      <>
        <Route path="/logout" element={<Logout />} />
        <Route path="*" element={<Unauthenticated />} />
      </>
    );
  } else if (
    !userDetails ||
    Object.keys(userDetails).length === 0 ||
    !userDetails.emailId ||
    !userDetails.isPdm
  ) {
    // Unauthorized
    return (
      <>
        <Route path="*" element={<Navigate to="/unauthorized" replace={true} />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
      </>
    );
  } else {
    // Authenticated and Authorized user
    return (
      <>
        <Route path="/signin/oidc" element={<Navigate to="/" replace={true} />} />
        <Route path="/signin/oidc-silent" element={<Navigate to="/" replace={true} />} />

        <Route path="/" element={<AppTabs />}>
          <Route index element={<Home />} />
          <Route path="manage" element={<MultiNumericFeatureManagement />} />
          <Route path="procos" element={<Procos />}>
            <Route path="features" element={<FeaturesMarketAssociation />} />
          </Route>
          <Route path="pdm" element={<Pdm />} />
          <Route path="categories" element={<Category />} />
          <Route path="logout" element={<Logout />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace={true} />} />
      </>
    );
  }
};
/**
 * Main component to handle the layout at the top level.
 * Sets the different routes.
 * @returns {JSX.Element} a container with the UI components at top level
 */
const AppRouter = () => {
  const dispatch = useDispatch<AppDispatch>();
  const auth = useAuth();
  const token = auth.userData?.access_token;

  const userDetails = useSelector((state: AppState) => state.user.details);

  useEffect(() => {
    dispatch(setActiveWorkItem(Master));
  }, [dispatch]);

  useEffect(() => {
    if (token) {
      dispatch(getLoggedInUserDetails({ token }));
    }
  }, [token, dispatch]);

  return (
    <Container maxWidth={false} className="appRouter root">
      <Header />
      <Box className="appRouterContent">
        <Routes>{getMiddleContent(token, userDetails)}</Routes>
      </Box>
      <UnsupportedBrowser />
      <CommonDialog />
      <LoadingScreen />
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={true}
        closeOnClick={false}
        pauseOnHover
        theme="colored"
        draggable
      />
    </Container>
  );
};

export default AppRouter;
